import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiClient } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import {
  createAnnotationFromDraft,
  type Annotation,
  type AnnotationDraft,
} from './annotationTypes';

interface RawAnnotationUnit {
  annotation?: string | null;
  end_offset?: number | string | null;
  id?: string | null;
  start_offset?: number | string | null;
  text?: string | null;
  text_color?: string | null;
  text_prefix?: string | null;
  text_suffix?: string | null;
  timestamp?: number | string | null;
}

interface AnnotationMutationParams {
  annotation: Annotation;
  componentId: string;
  contentUnitId: string;
  courseId: string;
}

export interface AnnotationService {
  createAnnotation(params: AnnotationMutationParams): Promise<Annotation>;
  deleteAnnotation(params: AnnotationMutationParams): Promise<void>;
  updateAnnotation(params: AnnotationMutationParams): Promise<Annotation>;
}

function toRawAnnotationUnit(annotation: Annotation): RawAnnotationUnit {
  const rawAnnotation: RawAnnotationUnit = {
    annotation: annotation.note,
    end_offset: annotation.quote.endOffset ?? undefined,
    id: annotation.id,
    start_offset: annotation.quote.startOffset ?? undefined,
    text: annotation.quote.exact,
    text_prefix: annotation.quote.prefix,
    text_suffix: annotation.quote.suffix,
    timestamp: annotation.timestamp,
  };

  if (annotation.textColor) {
    rawAnnotation.text_color = annotation.textColor;
  }

  return rawAnnotation;
}

export class KuduAnnotationService implements AnnotationService {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly userId: string,
  ) {}

  async createAnnotation(params: AnnotationMutationParams): Promise<Annotation> {
    await this.apiClient.invokeKuduMethod<
      Record<string, never>,
      {
        annotation_unit: RawAnnotationUnit;
        component_id: string;
        content_unit_id: string;
        course_id: string;
        user_id: string;
      }
    >('AddContentUnitAnnotation', {
      annotation_unit: toRawAnnotationUnit(params.annotation),
      component_id: params.componentId,
      content_unit_id: params.contentUnitId,
      course_id: params.courseId,
      user_id: this.userId,
    });

    return params.annotation;
  }

  async updateAnnotation(params: AnnotationMutationParams): Promise<Annotation> {
    return this.createAnnotation(params);
  }

  async deleteAnnotation(params: AnnotationMutationParams): Promise<void> {
    await this.apiClient.invokeKuduMethod<
      Record<string, never>,
      {
        annotation_unit: RawAnnotationUnit;
        component_id: string;
        content_unit_id: string;
        course_id: string;
        delete_annotation_unit: boolean;
        user_id: string;
      }
    >('AddContentUnitAnnotation', {
      annotation_unit: toRawAnnotationUnit(params.annotation),
      component_id: params.componentId,
      content_unit_id: params.contentUnitId,
      course_id: params.courseId,
      delete_annotation_unit: true,
      user_id: this.userId,
    });
  }
}

export function createAnnotationFromPayload(
  contentUnitId: string,
  draft: AnnotationDraft,
) {
  return createAnnotationFromDraft({
    ...draft,
    contentUnitId,
  });
}

export function useKuduAnnotationService(): AnnotationService | null {
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return useMemo(() => {
    if (!user) {
      return null;
    }

    const userId = getKuduUserId(user);

    if (!userId) {
      return null;
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const client = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });

    return new KuduAnnotationService(client, userId);
  }, [getIdToken, location.hash, location.pathname, location.search, navigate, user]);
}
